import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { allServiceTitles } from "../data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        service: form.service || null,
      });
      toast.success("Thank you! Our team will get back to you shortly.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or call us."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#f8f9fa] border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <span className="text-xs font-bold uppercase tracking-widest text-[#8b8f99]">
            Get in touch
          </span>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-black text-[#10193f]">
            Request a survey or a quote
          </h2>
          <p className="mt-4 text-[#52525b] max-w-md">
            Tell us about your project and our team will recommend the right
            aerial workflow and deliverables.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href="tel:+917715972997"
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#10193f] text-white">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8b8f99] font-semibold">
                  Call us
                </div>
                <div className="font-heading font-bold text-[#10193f] group-hover:text-[#1a2350]">
                  +91 7715972997
                </div>
              </div>
            </a>
            <a
              href="mailto:info@snesar.com"
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#10193f] text-white">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8b8f99] font-semibold">
                  Email
                </div>
                <div className="font-heading font-bold text-[#10193f]">
                  info@snesar.com
                </div>
              </div>
            </a>
            <div className="flex items-center gap-4" data-testid="contact-location">
              <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#10193f] text-white">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8b8f99] font-semibold">
                  Location
                </div>
                <div className="font-heading font-bold text-[#10193f]">
                  Navi Mumbai, Maharashtra
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 rounded-sm overflow-hidden border border-gray-200" data-testid="contact-map">
            <iframe
              title="Snesar Aerial Intelligence — Navi Mumbai"
              src="https://www.google.com/maps?q=Navi%20Mumbai%2C%20Maharashtra&output=embed"
              width="100%"
              height="240"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="bg-white border border-gray-200 rounded-sm p-7 sm:p-9 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#10193f] font-semibold">
                Full Name *
              </Label>
              <Input
                id="name"
                data-testid="input-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Your name"
                className="rounded-sm border-gray-300 focus-visible:ring-[#8b8f99]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#10193f] font-semibold">
                Phone *
              </Label>
              <Input
                id="phone"
                data-testid="input-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="Mobile number"
                className="rounded-sm border-gray-300 focus-visible:ring-[#8b8f99]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#10193f] font-semibold">
              Email *
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="input-email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="you@company.com"
              className="rounded-sm border-gray-300 focus-visible:ring-[#8b8f99]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#10193f] font-semibold">
              Service of Interest
            </Label>
            <Select
              value={form.service}
              onValueChange={(v) => update("service", v)}
            >
              <SelectTrigger
                data-testid="select-service"
                className="rounded-sm border-gray-300 focus:ring-[#8b8f99]"
              >
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {allServiceTitles.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`service-option-${t}`}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-[#10193f] font-semibold">
              Project Details *
            </Label>
            <Textarea
              id="message"
              data-testid="input-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Tell us about your site, location and requirements..."
              rows={4}
              className="rounded-sm border-gray-300 focus-visible:ring-[#8b8f99]"
            />
          </div>

          <Button
            type="submit"
            data-testid="contact-submit"
            disabled={loading}
            className="w-full h-12 rounded-sm bg-[#10193f] hover:bg-[#1a2350] text-white font-semibold text-base"
          >
            {loading ? "Sending..." : "Send Inquiry"}
            {!loading && <Send className="ml-2 h-4 w-4" />}
          </Button>
        </form>
      </div>
    </section>
  );
};
